import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Location } from '../../../location/LocationStep';
import { useSplitScreen } from '../SplitScreenContext';

interface SplitLocationDisplayProps {
  locations: { node: Location }[];
  selectedLocations: Location | Location[] | null;
  onLocationClick: (location: Location) => void;
  isClickable: boolean;
  nodeData: any;
}

// Light warm defaults — overridable via nodeData.settings.colors from backend
const defaultColors = {
  cardBackground: '#FFFFFF',
  cardBorder: 'rgba(0,0,0,0.07)',
  cardBorderSelected: '#DDB889',
  titleText: '#1C1208',
  subtitleText: '#786B5A',
  infoText: '#A09382',
  numberText: 'rgba(221,184,137,0.45)',
  selectionBackground: '#DDB889',
  accentGlow: 'rgba(221,184,137,0.07)',
};

const SplitLocationDisplay: React.FC<SplitLocationDisplayProps> = ({
  locations,
  selectedLocations,
  onLocationClick,
  isClickable,
  nodeData,
}) => {
  const { setContextPanel, setAllLocations } = useSplitScreen();
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  useEffect(() => {
    setAllLocations(locations.map(({ node }) => node));
  }, [locations]);

  const c = { ...defaultColors, ...(nodeData?.settings?.colors || {}) };

  const isSelected = (location: Location) => {
    if (!selectedLocations) return false;
    if (Array.isArray(selectedLocations)) return selectedLocations.some((s) => s.id === location.id);
    return selectedLocations.id === location.id;
  };

  const handleHoverStart = (location: Location) => {
    setHoveredId(location.id);
    setContextPanel({ type: 'location', data: location });
  };

  const handleHoverEnd = () => {
    setHoveredId(null);
    const selected = Array.isArray(selectedLocations) ? selectedLocations[0] : selectedLocations;
    setContextPanel(selected ? { type: 'location', data: selected } : null);
  };

  const handleClick = (location: Location) => {
    if (!isClickable) return;
    onLocationClick(location);
    setContextPanel({ type: 'location', data: location });
  };

  const activeId = hoveredId
    ?? (Array.isArray(selectedLocations) ? selectedLocations[0]?.id : selectedLocations?.id)
    ?? null;

  return (
    <div className="lp-flex lp-flex-col lp-gap-3">
      {locations.map(({ node: location }, index) => {
        const selected = isSelected(location);
        const isActive = activeId === location.id;
        const num = String(index + 1).padStart(2, '0');

        return (
          <motion.div
            key={location.id}
            className="lp-relative lp-cursor-pointer lp-rounded-2xl lp-overflow-hidden"
            style={{
              backgroundColor: c.cardBackground,
              border: `1.5px solid ${selected ? c.cardBorderSelected : isActive ? 'rgba(221,184,137,0.4)' : c.cardBorder}`,
              boxShadow: selected
                ? '0 4px 24px rgba(221,184,137,0.22)'
                : isActive
                ? '0 4px 16px rgba(221,184,137,0.1)'
                : '0 1px 4px rgba(0,0,0,0.05)',
              minHeight: isActive ? 148 : 108,
              transition: 'min-height 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
            }}
            onHoverStart={() => handleHoverStart(location)}
            onHoverEnd={handleHoverEnd}
            onClick={() => handleClick(location)}
            animate={{ opacity: activeId === null || isActive ? 1 : 0.5 }}
            transition={{ duration: 0.2 }}
          >
            {/* Warm glow overlay */}
            <AnimatePresence>
              {isActive && (
                <motion.div
                  className="lp-absolute lp-inset-0 lp-pointer-events-none"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    background: `linear-gradient(135deg, ${c.accentGlow} 0%, transparent 55%)`,
                  }}
                />
              )}
            </AnimatePresence>

            {/* Left accent bar */}
            <motion.div
              className="lp-absolute lp-left-0 lp-top-3 lp-bottom-3 lp-w-[3px] lp-rounded-full"
              animate={{
                backgroundColor: isActive || selected ? c.cardBorderSelected : 'transparent',
              }}
              transition={{ duration: 0.25 }}
            />

            <div className="lp-relative lp-flex lp-items-center lp-gap-5 lp-px-7 lp-py-6">
              {/* Index number */}
              <motion.span
                className="lp-font-boska lp-shrink-0 lp-select-none lp-leading-none"
                animate={{
                  color: isActive ? c.cardBorderSelected : c.numberText,
                  fontSize: isActive ? '30px' : '22px',
                }}
                transition={{ duration: 0.25 }}
              >
                {num}
              </motion.span>

              {/* Text */}
              <div className="lp-flex-1 lp-min-w-0">
                <motion.p
                  className="lp-font-boska lp-tracking-wide lp-leading-none"
                  animate={{
                    color: c.titleText,
                    fontSize: isActive ? '30px' : '22px',
                  }}
                  transition={{ duration: 0.25 }}
                >
                  {location.city}
                </motion.p>

                <motion.p
                  className="lp-text-sm lp-mt-1 lp-truncate"
                  animate={{ color: isActive ? c.subtitleText : c.infoText }}
                  transition={{ duration: 0.25 }}
                >
                  {location.address}
                </motion.p>

                <AnimatePresence>
                  {isActive && location.phone && (
                    <motion.p
                      className="lp-text-xs lp-mt-1"
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.2 }}
                      style={{ color: c.infoText }}
                    >
                      {location.phone}
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>

              {/* Status indicator */}
              <AnimatePresence mode="wait">
                {selected ? (
                  <motion.div
                    key="check"
                    className="lp-shrink-0"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <CheckCircle className="lp-w-6 lp-h-6" style={{ color: c.selectionBackground }} />
                  </motion.div>
                ) : (
                  <motion.div
                    key="arrow"
                    className="lp-shrink-0 lp-text-lg"
                    animate={{ opacity: isActive ? 0.7 : 0.2, x: isActive ? 4 : 0 }}
                    transition={{ duration: 0.2 }}
                    style={{ color: c.cardBorderSelected }}
                  >
                    →
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

export default SplitLocationDisplay;
